import {
  BodyText,
  Button,
  Callout,
  Checkbox,
  Collapsible,
  Field,
  FieldError,
  Search,
  Skeleton,
  toast,
} from "@/components/ui";
import { SearchIcon, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, type UseFormReturn, useFieldArray } from "react-hook-form";
import { useAssessment } from "../../network/useAssessment";
import { Icd10QuickActions } from "../Icd10QuickActions";
import { useIcd10CodeSearch } from "../network";
import { type Icd10QuickAction, toIcd10Coding } from "../quickActions";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";

/** A blank code row / cleared primary. */
const EMPTY_ICD10_CODE = { code: "", system: "", display: "", version: "" };

interface Metadata {
  system: string;
  version: string;
  score: number;
  display: string;
}

interface EpisodeOfCareRowProps {
  patientId: string;
  episodeOfCareTitle: string;
  encounterId: string;
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
  index: number;
}

export const EpisodeOfCareRow = ({
  patientId,
  episodeOfCareTitle,
  encounterId,
  form,
  index,
}: EpisodeOfCareRowProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: `icd10.childEncounters.${index}.icd10Codes`,
  });

  const {
    data: assessmentData,
    isLoading: isAssessmentLoading,
    isError: isAssessmentError,
    refetch: refetchAssessment,
  } = useAssessment(encounterId);

  const {
    data: icd10CodeResults,
    isLoading: isLoadingIcd10Codes,
    isError: isErrorIcd10Codes,
  } = useIcd10CodeSearch(patientId, searchTerm);

  useEffect(() => {
    if (isErrorIcd10Codes) {
      toast.error("Error loading ICD-10 codes, please try again.");
    }
  }, [isErrorIcd10Codes]);

  const options =
    icd10CodeResults?.matchedConcepts?.map((code) => ({
      label: `${code.id} - ${code.term}`,
      disabled: code.billable !== undefined && !code.billable,
      level: code.level,
      value: code.id,
      metadata: {
        system: code.system,
        display: code.term,
        version: code.version,
        score: code.score,
      },
    })) || [];

  // Drives the checkbox state below. Event handlers must NOT branch on this:
  // primary is shared across every episode of care, so the value captured at
  // render can already be stale by the time a handler runs. They read it fresh
  // with `getValues` instead.
  const primaryIcd10Code = form.watch("icd10.primaryIcd10Code");

  /**
   * Adds an administrative code without going through search. Reuses the empty
   * row the form starts with so the clinician isn't left with a blank code to
   * clean up, and mirrors the search flow by claiming primary when nothing has.
   */
  const applyQuickAction = async (action: Icd10QuickAction) => {
    const coding = toIcd10Coding(action);
    const emptyRowIndex = fields.findIndex((field) => !field.code);

    if (emptyRowIndex >= 0) {
      update(emptyRowIndex, coding);
    } else {
      append(coding);
    }

    if (!form.getValues("icd10.primaryIcd10Code").code) {
      form.setValue("icd10.primaryIcd10Code", coding);
    }
    // Trigger validation on the entire icd10 object to ensure consistency
    await form.trigger("icd10");
  };

  return (
    <div className="flex flex-col gap-2">
      <Collapsible title={episodeOfCareTitle} headerSize="sm" border="none">
        {isAssessmentLoading && <Skeleton className="w-full h-4" />}
        {isAssessmentError && (
          <Callout
            variant="danger"
            title="Error loading assessment"
            description="Please click here to try again."
            onClick={() => refetchAssessment()}
          />
        )}
        {!isAssessmentLoading && !isAssessmentError && assessmentData && (
          <BodyText size="sm" textColor="primary">
            Assessment: {assessmentData?.assessment?.description}
          </BodyText>
        )}
      </Collapsible>
      {fields.map((field, icdIndex) => {
        const value =
          field.code && field.display ? `${field.code} - ${field.display}` : "";
        return (
          <div key={field.id} className="flex flex-row gap-2 items-center">
            <div className="flex flex-1 flex-col gap-1">
              <Controller
                control={form.control}
                name={`icd10.childEncounters.${index}.icd10Codes.${icdIndex}.code`}
                render={({ fieldState }) => {
                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <Search
                        placeholder="Search codes"
                        isLoading={isLoadingIcd10Codes}
                        input={value}
                        onInputChange={(value) => setSearchTerm(value)}
                        options={options}
                        onItemSelected={async (item) => {
                          const selected = {
                            code: item.value,
                            system: (item.metadata as Metadata)?.system,
                            display: (item.metadata as Metadata)?.display,
                            version: (item.metadata as Metadata)?.version,
                          };
                          const currentPrimary = form.getValues(
                            "icd10.primaryIcd10Code",
                          );
                          const replacedPrimary =
                            Boolean(field.code) &&
                            field.code === currentPrimary.code;

                          update(icdIndex, selected);
                          setSearchTerm("");

                          // Nothing is primary yet, or this row was primary and
                          // its code just changed underneath it — either way
                          // primary follows the selection.
                          if (!currentPrimary.code || replacedPrimary) {
                            form.setValue("icd10.primaryIcd10Code", selected);
                          }

                          // Trigger validation on the entire icd10 object to ensure consistency
                          await form.trigger("icd10");
                        }}
                        leftIcon={SearchIcon}
                        aria-invalid={fieldState.invalid}
                      />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  );
                }}
              />
            </div>
            <Button
              variant="icon"
              size="sm"
              data-testid={`delete-icd10-code-${icdIndex}`}
              onClick={async () => {
                remove(icdIndex);

                // Otherwise primary keeps pointing at a code that is no longer
                // on the claim, with no row left to show or clear it.
                if (
                  field.code &&
                  field.code === form.getValues("icd10.primaryIcd10Code").code
                ) {
                  form.setValue("icd10.primaryIcd10Code", EMPTY_ICD10_CODE);
                }

                // Trigger validation on the entire icd10 object to ensure consistency
                await form.trigger("icd10");
              }}
            >
              <Trash2 className="text-link w-4 h-4" />
            </Button>
            <Checkbox
              id={`primary-${field.id}`}
              checked={Boolean(
                field.code && field.code === primaryIcd10Code.code,
              )}
              disabled={!field.code}
              onCheckedChange={async (checked) => {
                if (checked) {
                  form.setValue("icd10.primaryIcd10Code", {
                    code: field.code,
                    system: field.system,
                    display: field.display,
                    version: field.version,
                  });
                } else {
                  form.setValue("icd10.primaryIcd10Code", EMPTY_ICD10_CODE);
                }
                // Trigger validation on the entire icd10 object to ensure consistency
                await form.trigger("icd10");
              }}
            />
          </div>
        );
      })}
      <div className="flex flex-row flex-wrap gap-3 items-center">
        <Button variant="link" onClick={() => append(EMPTY_ICD10_CODE)}>
          Add New
        </Button>
        <Icd10QuickActions
          appliedCodes={fields.map((field) => field.code)}
          onSelect={applyQuickAction}
        />
      </div>
    </div>
  );
};
